import { useEffect, useState } from 'react';
import {
  CHAMPS_FICHE,
  GROUPES_FICHE,
  champsDuGroupe,
  type CandidatureDetail,
  type FicheRecap,
  type GroupeFiche,
} from '@rh/shared';
import { ErreurReseau, appel } from '../api';
import { BoutonExport } from './BoutonExport';

/**
 * Fiche récapitulative de contact (demande du 2026-09-22) : les six
 * renseignements qu'on emporte pour aller voir un candidat, saisis ici, puis
 * imprimés sur un PDF au logo Plaisirs Gourmands, filigrané « CONFIDENTIEL ».
 *
 * L'ÂGE s'y saisit depuis le 2026-09-22 après-midi, sur demande : il est noté
 * par le recruteur d'après ce que le candidat lui dit, **jamais demandé dans un
 * formulaire et jamais un critère** — ni filtre, ni motif d'écart, ni colonne
 * d'export. Les champs laissés vides s'impriment en pointillés, pour être
 * remplis au stylo pendant l'entretien.
 *
 * Les six champs partent ensemble, en un seul envoi : c'est un formulaire, pas
 * six boutons. Sans droit d'écriture (rôle lecteur, fiche anonymisée), la
 * fiche se lit mais ne s'ouvre pas.
 */
const GROUPES = Object.keys(GROUPES_FICHE) as GroupeFiche[];

export function FicheRecapitulative({
  fiche,
  modifiable,
  onChange,
}: {
  fiche: CandidatureDetail;
  modifiable: boolean;
  onChange: (maj: CandidatureDetail) => void;
}) {
  const [saisie, setSaisie] = useState<FicheRecap>(fiche.fiche);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState(false);

  // La fiche venue du serveur reprend la main (relecture, envoi d'un autre champ).
  useEffect(() => setSaisie(fiche.fiche), [fiche.fiche]);

  const modifie = CHAMPS_FICHE.some((champ) => (saisie[champ.cle] ?? '') !== (fiche.fiche[champ.cle] ?? ''));

  async function enregistrer(): Promise<void> {
    setEnvoi(true);
    setErreur(null);
    setEnregistre(false);
    try {
      const maj = await appel<CandidatureDetail>(`/candidatures/${fiche.id}/fiche`, {
        method: 'PUT',
        body: JSON.stringify(
          Object.fromEntries(CHAMPS_FICHE.map((champ) => [champ.cle, saisie[champ.cle]?.trim() || null])),
        ),
      });
      onChange(maj);
      setEnregistre(true);
    } catch (e) {
      setErreur(
        e instanceof ErreurReseau
          ? 'Enregistrement impossible sans connexion : réessayez une fois en ligne.'
          : e instanceof Error
            ? e.message
            : 'Enregistrement impossible',
      );
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <section className="bloc fiche-recap">
      <h3>Fiche récapitulative</h3>
      <p className="mention">
        Ce qui est saisi ici s’imprime sur la fiche ; ce qui reste vide s’imprime en pointillés, à
        remplir au stylo pendant l’entretien.
      </p>

      {modifiable ? (
        <>
          {GROUPES.map((groupe) => (
            <fieldset key={groupe} className="groupe-fiche">
              <legend>{GROUPES_FICHE[groupe]}</legend>
              <div className="champs-fiche">
                {champsDuGroupe(groupe).map((champ) => {
                  const identifiant = `fiche-${champ.cle}-${fiche.id}`;
                  const commun = {
                    id: identifiant,
                    maxLength: champ.max,
                    disabled: envoi,
                    value: saisie[champ.cle] ?? '',
                    onChange: (evenement: { target: { value: string } }) =>
                      setSaisie({ ...saisie, [champ.cle]: evenement.target.value }),
                  };
                  return (
                    <p key={champ.cle} className={champ.lignes > 1 ? 'champ-long' : 'champ-court'}>
                      <label htmlFor={identifiant}>{champ.libelle}</label>
                      {champ.lignes > 1 ? (
                        <textarea {...commun} rows={champ.lignes} />
                      ) : (
                        <input
                          {...commun}
                          type="text"
                          // Ce que la fiche sait déjà se propose sans s'imposer :
                          // la ville vient du CV ou du formulaire, le recruteur
                          // écrit ce que le candidat lui a dit.
                          placeholder={champ.cle === 'residence' ? (fiche.ville ?? '') : ''}
                        />
                      )}
                      {champ.cle === 'age' && (
                        <span className="hint">
                          D’après ce que le candidat a dit. Jamais un critère de tri ni d’écart.
                        </span>
                      )}
                    </p>
                  );
                })}
              </div>
            </fieldset>
          ))}
          <p className="mention">
            Ni sexe, ni situation familiale, ni donnée de santé : ces renseignements ne se notent
            nulle part.
          </p>
        </>
      ) : (
        <dl className="kv-fiche">
          {CHAMPS_FICHE.map((champ) => (
            <div key={champ.cle}>
              <dt>{champ.libelle}</dt>
              <dd className={fiche.fiche[champ.cle] ? '' : 'vide'}>
                {fiche.fiche[champ.cle] ?? 'à remplir sur la fiche imprimée'}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className="actions-fiche">
        {modifiable && (
          <button
            type="button"
            className="bouton"
            disabled={envoi || !modifie}
            onClick={() => void enregistrer()}
          >
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        )}
        <BoutonExport
          chemin={`/candidatures/${fiche.id}/fiche.pdf`}
          nomParDefaut="fiche-recapitulative.pdf"
          libelle="Télécharger la fiche (PDF)"
        />
      </div>
      {enregistre && !modifie && <p className="mention">Fiche enregistrée.</p>}
      {erreur && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
    </section>
  );
}
